import React, { useState, useEffect } from "react"; // eslint-disable-line no-unused-vars
import {
  Notification,
  SignInLink,
  SignUpLink,
  RequiredTextField,
} from "../../../shared/components";
import {
  claimCharityByCheck,
  claimCharityByCode,
  requestCharityClaimCode,
  getCharity,
} from "../../../shared/utilities/actions";
import {
  Button,
  Grid2 as Grid,
  LinearProgress,
  Typography,
  Paper,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { Charity, AddressUS } from "../../../shared/types";

export interface MainProps {}

export const Main = (props: MainProps): React.JSX.Element => {
  const [busy, setBusy] = useState<boolean>(false);
  const { isLoading, getAccessTokenSilently, user, isAuthenticated } =
    useAuth0();
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [identificationOption, setIdentificationOption] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [checkNumber1, setCheckNumber1] = useState<string>("");
  const [checkAmount1, setCheckAmount1] = useState<string>("");
  const [checkNumber2, setCheckNumber2] = useState<string>("");
  const [checkAmount2, setCheckAmount2] = useState<string>("");
  const [ein, setEin] = useState<string>("");
  const [charity, setCharity] = useState<Charity | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setBusy(true);
      let params = new URLSearchParams(document.location.search);
      let queryParameterEin = params.get("ein");
      if (queryParameterEin) {
        let charityResponse: Charity | null =
          await getCharity(queryParameterEin);
        setCharity(charityResponse);
        setEin(queryParameterEin);
      }
      setBusy(false);
    };
    fetchData();
  }, []);

  const renderSignIn = () => {
    return (
      <Grid size={{ xs: 12 }} container textAlign="center" spacing={1}>
        <Grid size={{ xs: 12 }} textAlign="center">
          <Typography variant="body1">
            You must be logged in to claim your charity.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <SignInLink label="Log In" disabled={busy} />
          <SignUpLink label="Create Account" disabled={busy} />
        </Grid>
      </Grid>
    );
  };

  const renderEIN = () => {
    return (
      <Grid size={{ xs: 12 }} container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body1" sx={{ paddingBottom: "8px" }}>
            Please enter your EIN.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <RequiredTextField
            disabled={busy}
            label="EIN"
            helperText=""
            value={ein}
            test={(value: string) => {
              return value ? "" : "You must provide a value for this field";
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEin(event.target.value.replace("-", "").trim());
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            variant="outlined"
            className="tb_action_button"
            disabled={!ein}
            onClick={async () => {
              setBusy(true);
              if (ein) {
                try {
                  let charityResponse: Charity | null = await getCharity(ein);
                  setCharity(charityResponse);
                } catch {
                  alert(
                    "We're unable to locate your charity. Please confirm you have provided the correct EIN."
                  );
                }
              }
              setBusy(false);
            }}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderDefault = () => {
    return (
      <Grid size={{ xs: 12 }} container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body1" sx={{ paddingBottom: "8px" }}>
            Please enter your EIN and the code you received from TisBest.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <RequiredTextField
            disabled={busy}
            label="EIN"
            helperText=""
            value={ein}
            test={(value: string) => {
              return value ? "" : "You must provide a value for this field";
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEin(event.target.value.replace("-", "").trim());
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <RequiredTextField
            disabled={busy}
            label="Code"
            helperText="Enter the code you received from TisBest, including dashes."
            value={code}
            test={(value: string) => {
              return value ? "" : "You must provide a value for this field";
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCode(event.target.value);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ marginTop: "8px" }}>
          <Button
            variant="outlined"
            className="tb_action_button"
            disabled={!ein}
            onClick={async () => {
              setBusy(true);
              if (ein) {
                try {
                  let charityResponse: Charity | null = await getCharity(ein);
                  setCharity(charityResponse);
                  if (code && charityResponse) {
                    let success = await claimCharityByCode(
                      ein,
                      code,
                      getAccessTokenSilently
                    );
                    setShowConfirmation(success);
                  }
                } catch {
                  alert(
                    "We're unable to locate your charity. Please confirm you have provided the correct EIN."
                  );
                }
              }
              setBusy(false);
            }}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderIdentificationOptions = () => {
    return (
      <Grid size={{ xs: 12 }} container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", paddingBottom: "8px" }}
          >
            If you are a representative of this organization, please claim your
            charity by selecting a verification option.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper
            sx={{
              width: "90%",
              padding: "16px",
              margin: "8px auto",
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => {
              setIdentificationOption("code");
            }}
          >
            <Typography
              variant="body1"
              sx={{ textAlign: "center", paddingBottom: "8px" }}
            >
              I received an access code.
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper
            sx={{
              width: "90%",
              padding: "16px",
              margin: "8px auto",
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => {
              setIdentificationOption("check");
            }}
          >
            <Typography
              variant="body1"
              sx={{ textAlign: "center", paddingBottom: "8px" }}
            >
              I've received multiple TisBest checks.
            </Typography>
          </Paper>
        </Grid>
        {/* <Grid size={{ xs: 12, sm: 4 }}>
          <Paper
            sx={{
              width: "300px",
              height: "100px",
              padding: "16px",
              paddingTop: "24px",
              margin: "8px auto",
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => {
              setIdentificationOption("none");
            }}
          >
            <Typography
              variant="body1"
              sx={{ textAlign: "center", paddingBottom: "8px" }}
            >
              I don't have check info or an access code.
            </Typography>
          </Paper>
        </Grid> */}
      </Grid>
    );
  };

  const renderConfirmation = () => {
    return (
      <Grid size={{ xs: 12 }} container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body1" sx={{ paddingBottom: "8px" }}>
            {identificationOption == "none"
              ? `We're processing your request. You will receive a confirmation email from us shortly.`
              : `We're processing your request. If you submitted accurate information you'll receive a Welcome email typically within 24 hours with a link to your Charity Dashboard. Please check your spam folder if you're having any trouble finding the charities@tisbest.org email.`}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            onClick={async () => {
              window.location.href = `/charity/?charityId=${ein}`;
            }}
          >
            Visit Charity Profile Page
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderIdentificationCheck = () => {
    return (
      <Grid size={{ xs: 12 }} container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body1" sx={{ paddingBottom: "8px" }}>
            Provide check numbers and donation amounts for two checks your
            charity has received from TisBest. They must be dated April 2023 or
            later and have been cashed.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <img
            src="https://qatisbestdata.blob.core.windows.net/images/Example_Check.png"
            style={{ width: "100%" }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              disabled={busy}
              label="Check Number 1"
              helperText="This can be found at the top right of your check."
              value={checkNumber1}
              test={(value: string) => {
                return value ? "" : "You must provide a value for this field";
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setCheckNumber1(event.target.value);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              disabled={busy}
              label="Amount 1"
              helperText="The full amount on the check."
              value={checkAmount1}
              test={(value: string) => {
                return value ? "" : "You must provide a value for this field";
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setCheckAmount1(event.target.value);
              }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              disabled={busy}
              label="Check Number 2"
              helperText="This can be found at the top right of your check."
              value={checkNumber2}
              test={(value: string) => {
                return value ? "" : "You must provide a value for this field";
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setCheckNumber2(event.target.value);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              disabled={busy}
              label="Amount 2"
              helperText="The full amount on the check."
              value={checkAmount2}
              test={(value: string) => {
                return value ? "" : "You must provide a value for this field";
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setCheckAmount2(event.target.value);
              }}
            />
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ marginTop: "8px", textAlign: "right" }}>
          <Button
            variant="outlined"
            className="tb_cancel_button"
            onClick={async () => {
              setIdentificationOption("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            className="tb_action_button"
            disabled={
              !checkNumber1 ||
              !checkAmount1 ||
              !checkNumber2 ||
              !checkAmount2 ||
              !ein
            }
            onClick={async () => {
              // if (
              //   confirm(
              //     "Please make sure that the information you have entered is accurate."
              //   )
              // ) {
              setBusy(true);
              if (
                checkNumber1 ||
                checkAmount1 ||
                checkNumber2 ||
                checkAmount2 ||
                ein
              ) {
                let success = await claimCharityByCheck(
                  ein,
                  {
                    checkNumber1: checkNumber1,
                    checkAmount1: checkAmount1,
                    checkNumber2: checkNumber2,
                    checkAmount2: checkAmount2,
                  },
                  getAccessTokenSilently
                );
                setShowConfirmation(success);
              }

              setBusy(false);
              // }
            }}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderIdentificationCode = () => {
    return (
      <Grid size={{ xs: 12 }} container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body1" sx={{ paddingBottom: "8px" }}>
            Enter the code you received from TisBest, including dashes.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <RequiredTextField
            disabled={busy}
            label="Code"
            helperText="Enter the code you received from TisBest, including dashes."
            value={code}
            test={(value: string) => {
              return value ? "" : "You must provide a value for this field";
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCode(event.target.value);
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }} style={{ marginTop: "8px" }}>
          <Button
            variant="outlined"
            className="tb_cancel_button"
            onClick={async () => {
              setIdentificationOption("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            className="tb_action_button"
            disabled={!code || !ein}
            onClick={async () => {
              // if (
              //   confirm(
              //     "Please make sure that the information you have entered is accurate."
              //   )
              // ) {
              setBusy(true);
              if (code && ein) {
                let success = await claimCharityByCode(
                  ein,
                  code,
                  getAccessTokenSilently
                );
                setShowConfirmation(success);
              }
              setBusy(false);
              // }
            }}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderAddress = (charity: Charity) => {
    if (charity.managed.distributionAddress) {
      let distributionAddress: AddressUS = JSON.parse(
        charity.managed.distributionAddress
      );
      return (
        <Typography variant="body1" component="div">
          <div>{charity.source.name}</div>
          <div>{distributionAddress.company}</div>
          <div>{distributionAddress.street}</div>
          <div>
            {distributionAddress.city}&nbsp;{distributionAddress.state},&nbsp;
            {distributionAddress.zip}
          </div>
        </Typography>
      );
    } else {
      return (
        <Typography variant="body1" component="div">
          <div>{charity.source.name}</div>
          <div>{charity.source.street}</div>
          <div>
            {charity.source.city}&nbsp;{charity.source.state},&nbsp;
            {charity.source.zip}
          </div>
        </Typography>
      );
    }
  };

  const renderIdentificationNone = () => {
    return (
      <Grid size={{ xs: 12 }} container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body1" sx={{ paddingBottom: "8px" }}>
            The current mailing address we have on file for your charity is
            listed below. If you can receive mail there please select the submit
            button and we'll mail you a code to complete this process and claim
            your charity. If you cannot receive mail at this address email us at
            &nbsp;
            <a href="mailto:charities@tisbest.org">charities@tisbest.org</a> for
            assistance. For security reasons please send this request from an
            email address associated with your charity website. Include your
            charity’s EIN and new mailing address.
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>{charity && renderAddress(charity)}</Grid>
        <Grid size={{ xs: 12 }} style={{ marginTop: "8px" }}>
          <Button
            variant="outlined"
            className="tb_cancel_button"
            onClick={async () => {
              setIdentificationOption("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            className="tb_action_button"
            onClick={async () => {
              setBusy(true);
              let success = await requestCharityClaimCode(
                ein,
                getAccessTokenSilently
              );
              setShowConfirmation(success);
              setBusy(false);
            }}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderIdentificationInputs = () => {
    switch (identificationOption) {
      case "check":
        return renderIdentificationCheck();
      case "code":
        return renderIdentificationCode();
      case "none":
        return renderIdentificationNone();
      default:
        return renderIdentificationOptions();
    }
  };

  return (
    <Paper sx={{ padding: "24px", margin: "auto", maxWidth: "900px" }}>
      <Notification
        message={notification.message}
        type={notification.type}
        open={notification.message.length > 0}
        onDismiss={() => {
          setNotification({ message: "", type: "" });
        }}
      />
      {busy || isLoading ? (
        <LinearProgress />
      ) : (
        <Grid size={{ xs: 12 }} container>
          {isAuthenticated ? (
            charity ? (
              <Grid size={{ xs: 12 }} container spacing={1}>
                {showConfirmation
                  ? renderConfirmation()
                  : renderIdentificationInputs()}
              </Grid>
            ) : (
              renderDefault()
            )
          ) : (
            renderSignIn()
          )}
        </Grid>
      )}
    </Paper>
  );
};
